import {defineSlotRecipe} from '@chakra-ui/react3'

/**
 * Slot recipe for the flyout's tool navigation — the row of secondary links and
 * the country selector that closes the navigation panel (STOK-17026 §5).
 *
 * The row itself is viewport-agnostic: links pinned to the start, country
 * selector to the end, on both viewports. Only the two controls change size
 * across the breakpoint, and they do that through their own recipe variants
 * (`size` on `Button`), not here — see `flyout-navigation.recipe.js` for where
 * the row is placed within the panel.
 */
export const flyoutToolNavRecipe = defineSlotRecipe({
    className: 'flyout-tool-nav',
    slots: ['root', 'links', 'countrySelector'],
    base: {
        root: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'spacing-16'
        },
        links: {
            display: 'flex',
            alignItems: 'center',
            // Desktop hands the row `pointer-events: none` so its empty middle
            // does not swallow clicks on the navigation content beneath (see
            // `toolNavContainer` in `flyout-navigation.recipe.js`); the controls
            // opt back in here, where they can be named directly rather than
            // matched by tag from the placement slot.
            pointerEvents: 'auto',
            // WCAG 2.5.8: the labels are only 20px tall, so they clear the
            // 24px target minimum by the spacing exception — this gap is what
            // keeps their target circles from intersecting. Not decoration.
            gap: 'spacing-24'
        },
        countrySelector: {
            pointerEvents: 'auto',
            // A flag is artwork, not a monochrome glyph, so the square `_icon`
            // box every button size sets would letterbox it. Only the width is
            // given here; the height follows the flag's own `viewBox`, which
            // keeps each artwork undistorted at the design-system flag-icon
            // widths (XSmall/Small).
            //
            // `&&` doubles the class so this outranks the button recipe's own
            // `_icon` rule. Needed because `size` here is responsive: that puts
            // the recipe's values in media blocks which never merge with these
            // keys, so both reach the stylesheet at equal specificity and order
            // would decide. Measured — without the doubling the flag renders as
            // a 16px square. See docs/agents/recipes.md.
            '&&': {
                _icon: {
                    width: {base: 'spacing-26', s: 'spacing-22'},
                    height: 'auto',
                    borderRadius: '2xs'
                }
            }
        }
    }
})
